var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import axios from 'axios';
import { AuthenticationResult, AuthenticationStatus, AuthenticationReason } from '@mc-alberta/types';
import { merge } from 'lodash';
import { getTypesByStrategy } from '../sdk/utils';
import { AuthenticationStrategy } from '../types/enums/AuthenticationStrategy';
import { Kpi } from '../loggers/Kpi';
import { KpiOperation } from '../types/enums/KpiOperation';
export class Adapter {
    /** Whether or not this strategy can be enrolled into. */
    get isEnrollable() {
        return false;
    }
    /** Whether or not an enrollment into the current strategy is eligible but not yet performed. */
    get isEnrollmentPending() {
        return this.isEnrollable && this.identityVerifications.length > 0;
    }
    /** The actively-selected authentication method. */
    get authenticationMethod() {
        return this.authenticationMethods.length === 1
            ? this.authenticationMethods[0]
            : this.authenticationMethods[this.selectedMethodIndex];
    }
    /** True if the strategy can be used for authentication. */
    get isUsable() {
        return this.authenticationMethods.length > 0 && this
            .authenticationContext
            .authenticationReasons
            .reduce((isUsable, reason) => {
            return this.supportedReasons.includes(reason) || isUsable;
        }, false);
    }
    /** True if this is PA (payment auth). False when NPA (non-payment auth). */
    get isPaymentAuthentication() {
        return this
            .authenticationContext
            .authenticationReasons
            .includes(AuthenticationReason.TRANSACTION_AUTHENTICATION);
    }
    constructor(params) {
        /** UI-defined authentication method attributes. */
        this.methodAttributes = {};
        /** Current status of the active authentication session. */
        this.authenticationStatus = AuthenticationStatus.NOT_SUPPORTED;
        /** Whether or not authentication has successfully completed. */
        this.authenticationResult = AuthenticationResult.NOT_AUTHENTICATED;
        /** List of usable authentication methods belonging to this strategy. */
        this.authenticationMethods = [];
        /** The index of the actively-selected authentication method. */
        this.selectedMethodIndex = -1;
        /** Whether or not a challenge is required. */
        this.isChallengeRequired = false;
        /** A list of authenticated ID&V states performed by all adapters in the Stark instance. */
        this.identityVerifications = [];
        /** Whether or not ID&V was performed for this specific strategy. */
        this.isIdentityVerificationPerformed = false;
        /** List of supported authentication reasons for this strategy. */
        this.supportedReasons = Object.values(AuthenticationReason);
        /**
         * Applies the given authentication methods for this adapter.
         */
        this.applyAuthenticationMethods = (authenticationMethods) => {
            const types = getTypesByStrategy(this.strategy);
            if (authenticationMethods) {
                this.authenticationMethods = authenticationMethods.filter(method => {
                    return types.includes(method.authenticationMethodType);
                });
                if (this.authenticationMethods.length === 1) {
                    this.selectedMethodIndex = 0;
                }
            }
            this.clearSession();
        };
        /**
         * Bootstraps the authentication methods for this adapter.
         */
        this.bootstrap = this.applyAuthenticationMethods.bind(this);
        /**
         * Selects the authentication method having the given index.
         */
        this.selectAuthenticationMethod = (index) => {
            this.selectedMethodIndex = index;
            this.authenticationStatus = AuthenticationStatus.PENDING;
        };
        /**
         * Initializes an authentication session.
         */
        this.initializeSession = () => this.getSession();
        /**
         * Provides the challenge to the user.
         *
         * @param data - strategy-specific data (e.g., FIDO username, DOM selector, OTP value, etc.)
         */
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        this.provideChallenge = (data) => {
            this.kpi.start(KpiOperation.PROVIDE_AUTHENTICATION_CHALLENGE);
            return this.getSession();
        };
        /**
         * Validates the authentication session.
         */
        this.validateSession = () => {
            this.kpi.start(KpiOperation.COMPLETE_AUTHENTICATION);
            return this.getSession();
        };
        /**
         * Returns the current, locally-cached authentication session status.
         * This does not make a request to the authentication service.
         */
        this.getSession = () => Promise.resolve({
            authenticationResult: this.authenticationResult,
            authenticationStatus: this.authenticationStatus,
            assuranceData: this.assuranceData
        });
        /**
         * Fetches the current authentication session information from the authentication service.
         *
         * @param methodAttributes - additional method-specific attributes to send with the request
         * @param handleError - whether or not to automatically convert XHR errors into `NOT_SUPPORTED`
         * @param payload - any overrides to provide to the authentication
         */
        this.refreshSession = (methodAttributes = {}, payload = {}, handleError = true) => __awaiter(this, void 0, void 0, function* () {
            if ([
                AuthenticationStatus.NOT_SUPPORTED,
                AuthenticationStatus.EXPIRED,
                AuthenticationStatus.CHALLENGE_FAILED,
                AuthenticationStatus.CANCELLED
            ].includes(this.authenticationStatus)) {
                return this.getSession();
            }
            try {
                const method = this.authenticationMethod;
                const data = yield this.apiRequest('/api/accountholder/authentications/authenticate', Object.assign(Object.assign({}, payload), { authenticationMethod: Object.assign(Object.assign({}, this.authenticationMethod), { methodAttributes: Object.assign(Object.assign({}, this.authenticationMethod.methodAttributes), methodAttributes) }) }));
                this.assuranceData = data.assuranceData;
                this.authenticationResult = data.authenticationResult;
                this.authenticationStatus = data.authenticationStatus;
                this.authenticationSessionId = data.authenticationSessionId;
                method.methodAttributes = merge(method.methodAttributes, data.methodAttributes);
            }
            catch (error) {
                if (handleError) {
                    this.error = error;
                    return this.destroySession(error);
                }
                return Promise.reject(error);
            }
            return this.getSession();
        });
        /**
         * Binds the successfully-authenticated session (e.g., 3DS, FIDO2 authenticator)
         * to the account reference in question.
         *
         * Session binding will only occur if the strategy in question performed ID&V.
         *
         * @returns true if the session was successfully bound; false otherwise
         */
        this.bindSession = () => __awaiter(this, void 0, void 0, function* () {
            if (this.isIdentityVerificationPerformed) {
                try {
                    yield this.apiRequest('/api/accountholder/authentications/token/bind', {
                        srcDigitalCardId: this.accountReference.srcDigitalCardId,
                        assuranceData: this.assuranceData
                    });
                    return true;
                }
                catch (error) {
                    // TODO: probably good to log this error
                }
            }
            return false;
        });
        /**
         * Destroys the current adapter session.
         */
        this.destroySession = (error) => {
            this.authenticationStatus = AuthenticationStatus.NOT_SUPPORTED;
            this.authenticationResult = AuthenticationResult.NOT_AUTHENTICATED;
            if (error) {
                this.kpi.queueError(error);
            }
            return this.getSession();
        };
        /**
         * Resets the current authentication session back to its original state.
         */
        this.clearSession = () => {
            this.authenticationStatus = this.isUsable
                ? AuthenticationStatus.PENDING
                : AuthenticationStatus.NOT_SUPPORTED;
            this.authenticationResult = AuthenticationResult.NOT_AUTHENTICATED;
            this.isIdentityVerificationPerformed = false;
            delete this.authenticationSessionId;
            delete this.assuranceData;
            delete this.error;
            return this.getSession();
        };
        /**
         * Makes a POST API request using the Axios adapter instance.
         */
        this.apiRequest = (endpoint, payload) => __awaiter(this, void 0, void 0, function* () {
            const headers = {
                'x-src-cx-flow-id': this.traceId
            };
            if (this.cardToken) {
                headers['x-src-card-token'] = this.cardToken;
            }
            const { data } = yield axios.post(endpoint, Object.assign({ accountReference: this.accountReference, authenticationContext: this.authenticationContext, srcCorrelationId: this.srcCorrelationId, srcClientId: this.srcClientId, srciTransactionId: this.srciTransactionId, serviceId: this.serviceId, authenticationSessionId: this.authenticationSessionId }, payload), { headers });
            return data;
        });
        /**
         * Adapter-specific KPI meta data.
         */
        this.kpiMeta = () => {
            var _a;
            return ({
                cardData: {
                    cardVerificationData: {
                        cardAuthVerificationType: (_a = this
                            .authenticationMethod) === null || _a === void 0 ? void 0 : _a.authenticationMethodType
                    }
                }
            });
        };
        /**
         * KPI Formatter function
         */
        this.onKpiSuccess = () => {
            const strategyMap = {
                [AuthenticationStrategy.THREE_DS]: 'threeDs',
                [AuthenticationStrategy.MDES]: 'consent',
                [AuthenticationStrategy.FIDO2]: 'fido',
                [AuthenticationStrategy.LVLR]: 'lvlr'
            };
            return {
                cardData: {
                    cardVerificationData: {
                        cardAuthVerificationStatus: this.authenticationStatus,
                        cardAuthVerificationResult: this.authenticationResult,
                        cardAuthVerificationId: this.authenticationSessionId,
                        [`${strategyMap[this.strategy]}AuthVerificationSupported`]: true
                    }
                }
            };
        };
        this.traceId = params.traceId;
        this.serviceId = params.serviceId;
        this.srcClientId = params.srcClientId;
        this.srcCorrelationId = params.srcCorrelationId;
        this.srciTransactionId = params.srciTransactionId;
        this.accountReference = params.accountReference;
        this.authenticationContext = params.authenticationContext;
        this.suppressChallenge = params.suppressChallenge;
        this.kpi = new Kpi(params, this.kpiMeta.bind(this));
    }
}
//# sourceMappingURL=Adapter.js.map